import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetDescription 
} from "./ui/sheet";
import { 
  Sparkles, 
  DollarSign, 
  Home, 
  ShoppingCart, 
  PiggyBank, 
  Equal,
  TrendingUp,
  TrendingDown,
  Minus
} from "lucide-react";

export const SpendingBreakdownSheet = ({ open, onOpenChange, leftover, formatCurrency }) => {
  if (!leftover) return null;

  const { detailed_breakdown, trends } = leftover;
  const hasItems = detailed_breakdown && (
    detailed_breakdown.income_items?.length > 0 ||
    detailed_breakdown.bill_items?.length > 0 ||
    detailed_breakdown.essential_items?.length > 0 ||
    detailed_breakdown.goal_items?.length > 0
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-primary" />
            Spending Money Breakdown
          </SheetTitle>
          <SheetDescription>
            How your spending money is calculated for {leftover.month || 'this month'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
          {/* Summary Calculation */}
          <div className="space-y-3 pb-6 border-b">
            <SummaryRow 
              icon={<DollarSign className="w-4 h-4 text-emerald-600" />}
              bgColor="bg-emerald-100 dark:bg-emerald-900/30"
              label="Total Income"
              amount={leftover.total_income}
              formatCurrency={formatCurrency}
              trendPercent={trends?.income_trend}
              isPositive
              trendPositiveIsGood
            />
            <SummaryRow 
              icon={<Home className="w-4 h-4 text-orange-600" />}
              bgColor="bg-orange-100 dark:bg-orange-900/30"
              label="Recurring Bills"
              amount={leftover.recurring_bills}
              formatCurrency={formatCurrency}
              trendPercent={trends?.bills_trend}
            />
            <SummaryRow 
              icon={<ShoppingCart className="w-4 h-4 text-blue-600" />}
              bgColor="bg-blue-100 dark:bg-blue-900/30"
              label="Essential Expenses"
              amount={leftover.essential_expenses}
              formatCurrency={formatCurrency}
              trendPercent={trends?.essentials_trend}
            />
            <SummaryRow 
              icon={<PiggyBank className="w-4 h-4 text-purple-600" />}
              bgColor="bg-purple-100 dark:bg-purple-900/30"
              label="Savings Goals"
              amount={leftover.savings_goals}
              formatCurrency={formatCurrency}
            />

            {/* Equals Line */}
            <div className="flex items-center gap-2 pt-3 border-t">
              <Equal className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold">Spending Money</span>
              {trends?.leftover_trend !== 0 && trends?.leftover_trend != null && (
                <TrendBadge percent={trends.leftover_trend} positiveIsGood />
              )}
              <span className={`ml-auto font-bold text-lg tabular-nums ${
                leftover.status === "on_track" 
                  ? "text-emerald-600" 
                  : leftover.status === "tight"
                    ? "text-amber-600"
                    : "text-red-600"
              }`}>
                {formatCurrency(leftover.leftover)}
              </span>
            </div>
          </div>

          {/* Detailed Breakdown Sections */}
          {hasItems ? (
            <div className="space-y-5">
              <BreakdownSection 
                title="Income Sources" 
                items={detailed_breakdown.income_items} 
                formatCurrency={formatCurrency}
                colorClass="text-emerald-600"
                isPositive
                showTrends
                trendPositiveIsGood
              />
              <BreakdownSection 
                title="Recurring Bills" 
                items={detailed_breakdown.bill_items} 
                formatCurrency={formatCurrency}
                colorClass="text-red-600"
              />
              <BreakdownSection 
                title="Essential Spending" 
                items={detailed_breakdown.essential_items} 
                formatCurrency={formatCurrency}
                colorClass="text-red-600"
                showTrends
              />
              <BreakdownSection 
                title="Savings Goals" 
                items={detailed_breakdown.goal_items} 
                formatCurrency={formatCurrency}
                colorClass="text-purple-600"
              />
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No transactions or bills found for this month.</p>
              <p className="text-sm mt-1">Connect a bank account to see your breakdown.</p>
            </div>
          )}

          {/* Daily Budget Info */}
          <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Daily Budget</p>
                <p className="text-sm text-muted-foreground">
                  {leftover.days_remaining} days remaining
                </p>
              </div>
              <p className="text-2xl font-bold text-primary tabular-nums">
                {formatCurrency(leftover.leftover_daily)}/day
              </p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const TrendBadge = ({ percent, positiveIsGood = false }) => {
  if (percent === 0 || percent == null) return null;
  
  const isUp = percent > 0;
  const isGood = positiveIsGood ? isUp : !isUp;
  
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full ${
      isGood 
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    }`}>
      {isUp ? (
        <TrendingUp className="w-3 h-3" />
      ) : (
        <TrendingDown className="w-3 h-3" />
      )}
      {Math.abs(percent)}%
    </span>
  );
};

const SummaryRow = ({ icon, bgColor, label, amount, formatCurrency, trendPercent, isPositive = false, trendPositiveIsGood = false }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl ${bgColor} flex items-center justify-center`}>
        {icon}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-medium">{label}</span>
        <TrendBadge percent={trendPercent} positiveIsGood={trendPositiveIsGood} />
      </div>
    </div>
    <span className={`font-semibold tabular-nums ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
      {isPositive ? '+' : '-'}{formatCurrency(amount)}
    </span>
  </div>
);

const BreakdownSection = ({ title, items, formatCurrency, colorClass, isPositive = false, showTrends = false, trendPositiveIsGood = false }) => {
  if (!items || items.length === 0) return null;
  
  return (
    <div>
      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
        {title}
      </h4>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <span className="text-sm">{item.name}</span>
              {showTrends && item.trend_percent !== 0 && item.trend_direction !== 'same' && (
                <TrendBadge percent={item.trend_percent} positiveIsGood={trendPositiveIsGood} />
              )}
            </div>
            <span className={`text-sm font-medium tabular-nums ${colorClass}`}>
              {isPositive ? '+' : '-'}{formatCurrency(item.amount)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
